/* eslint-disable import/prefer-default-export */
import { useEffect } from 'react'
import { cloneDeep } from 'lodash'
import { useAppDispatch } from 'app/hooks'
import { useDashboardData, useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { getWidgetForDashboard } from 'features/widget/asyncThunks'
import { WidgetGuard } from 'features/types'
import { toDeltaFilter, toFilter } from 'utils/widgetUtil'
import { createMetric } from 'utils/runSearchUtil'
import { Dashboard } from 'schemas/DashboardSchema'
import { SocialWebBrandPassionIndexWidget } from 'schemas/SocialWebBrandPassionIndexWidgetSchema'
import { RunSearch } from 'schemas/action/RunSearchSchema'
import { Filter } from 'schemas/FilterSchema'
import { TopicInfo } from 'schemas/TopicInfoSchema'
import MetricSeries from 'enums/MetricSeries'
import MetricTimeUnit from 'enums/MetricTimeUnit'
import TimeInterval, { TimeIntervalStrings } from 'enums/TimeInterval'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'

const METRIC_SERIES = [MetricSeries.TOTAL_BUZZ, MetricSeries.NET_SENTIMENT, MetricSeries.PASSION]
const MINUTE_IN_MS = 60 * 1000
const HOUR_IN_MS = 60 * MINUTE_IN_MS

const getEndDateCorrectedFilter = (filter: Filter, timeInterval: TimeIntervalStrings) => {
  const endDateCorrectedFilter = cloneDeep(filter)
  let endDate = filter.publishedDate!.end!
  const correctTimePeriod = timeInterval === TimeInterval.LAST_HOUR ? MINUTE_IN_MS : HOUR_IN_MS

  while (endDate > new Date().getTime()) {
    endDate -= correctTimePeriod
    endDateCorrectedFilter.publishedDate!.end = endDate
  }

  return endDateCorrectedFilter
}

const createSearch = (
  dashboard: Dashboard,
  widgetSetting: SocialWebBrandPassionIndexWidget,
  filter: Filter,
  topicId: number
) => {
  const { id: dashboardId, name } = dashboard
  const { precision } = widgetSetting

  return createMetric(dashboardId!, name!, precision!, [topicId!], filter, METRIC_SERIES, [
    MetricTimeUnit.ALL
  ])
}

const RunSearchEffect: WidgetGuard = ({ widgetId, children }) => {
  const widgetSetting = useDashboardWidgetSetting<SocialWebBrandPassionIndexWidget>(widgetId)
  const dashboard = useDashboardData() as Dashboard
  const dispatch = useAppDispatch()
  const refreshTimestamp = useRefreshTimestamp(widgetId)

  useEffect(() => {
    const { timeInterval } = widgetSetting
    const filter = toFilter(widgetSetting)
    const endDateCorrectedFilter = getEndDateCorrectedFilter(filter, timeInterval!)
    const deltaFilter = toDeltaFilter(endDateCorrectedFilter)
    const secondDeltaFilter = toDeltaFilter(deltaFilter)
    const searches: RunSearch[] = []

    widgetSetting.topicInfos!.forEach((topicInfo: TopicInfo) => {
      const { topicId } = topicInfo

      searches.push(createSearch(dashboard, widgetSetting, filter, topicId!))
      if (widgetSetting.trendOption! >= 1 && deltaFilter.publishedDate!.start! >= 0) {
        searches.push(createSearch(dashboard, widgetSetting, deltaFilter, topicId!))
      }
      if (widgetSetting.trendOption! >= 2 && secondDeltaFilter.publishedDate!.start! >= 0) {
        searches.push(createSearch(dashboard, widgetSetting, secondDeltaFilter, topicId!))
      }
    })

    dispatch(
      getWidgetForDashboard({
        searches,
        widgetId: widgetSetting.widgetId!,
        timestamp: refreshTimestamp
      })
    )
  }, [dashboard, widgetSetting, dispatch, refreshTimestamp])

  return children
}

export default RunSearchEffect
